<template>
  <div
    ref="scrollContainer"
    class="mb-16 scrollbar-hide h-96 overflow-y-scroll rounded-2xl border dark:border-slate-600 dark:bg-kekkle-dark-light"
  >
    <comment
      v-for="(comment, index) in comments"
      :key="`${comment.author}-${index}`"
      :author-uuid="comment.author"
      :date="comment.created_at"
      :previous-date="getPreviousDate(index)"
      :comment="comment.comment"
    />
  </div>

  <div class="fixed bottom-0 left-0 flex w-full justify-center">
    <div class="w-full max-w-xl bg-white px-2 pb-4 dark:bg-kekkle-dark">
      <comment-input
        :posting-comment="posting"
        @post-comment="postNewComment"
      ></comment-input>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, watch, nextTick } from "vue";
import { useAuthStore } from "@/stores/modules/auth/auth";
import CommentInput from "@/components/CommentInput.vue";
import Comment from "@/components/Comment.vue";
import { logEvent } from "firebase/analytics";
import { firebaseAnalytics } from "@/helpers/firebase/firebase";
import { useComments } from "@/composables/useComments";
import { eventCommentDocumentConverter } from "@kekkle/shared/convertors/web/comment-document.converter";

type Props = {
  groupId: string;
  eventId: string;
};

const props = defineProps<Props>();

const authStore = useAuthStore();

const scrollContainer = ref<HTMLDivElement | null>(null);

const {
  comments,
  posting,
  postComment: postCommentToFirestore,
} = useComments({
  groupId: props.groupId,
  itemId: props.eventId,
  collectionName: "events_comments",
  converter: eventCommentDocumentConverter,
  authorUuid: authStore.authUuid ?? "",
});

watch(
  () => comments.value.length,
  () => {
    scrollToBottom();
  },
  { immediate: true },
);

function getPreviousDate(index: number): Date | undefined {
  if (index === 0) return undefined;
  const previousComment = comments.value[index - 1];
  return previousComment?.created_at;
}

function scrollToBottom() {
  nextTick(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  });
}

async function postNewComment(e: { comment: string }) {
  logEvent(firebaseAnalytics, "added_comment", { for: "event" });
  await postCommentToFirestore(e.comment);
  scrollToBottom();
}
</script>
